import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  Page,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { TimeOffPolicyCarryoverExpiration } from "@/pages/settings/time-off/policy/components/TimeOffPolicyCarryoverExpiration.tsx";
import { TimeOffPolicyLimits } from "@/pages/settings/time-off/policy/components/TimeOffPolicyLimits.tsx";
import { TimeOffPolicyMilestones } from "@/pages/settings/time-off/policy/components/TimeOffPolicyMilestones.tsx";
import { TimeOffPolicyMilestoneTransfer } from "@/pages/settings/time-off/policy/components/TimeOffPolicyMilestoneTransfer.tsx";
import { TimeOffPolicyRequireConfirmation } from "@/pages/settings/time-off/policy/components/TimeOffPolicyRequireConfirmation.tsx";
import { TimeOffPolicyStartBalance } from "@/pages/settings/time-off/policy/components/TimeOffPolicyStartBalance.tsx";
import { TimeOffPolicyTerminationAccrual } from "@/pages/settings/time-off/policy/components/TimeOffPolicyTerminationAccrual.tsx";
import { TimeOffPolicyVersionsTimeline } from "@/pages/settings/time-off/policy/components/TimeOffPolicyVersionsTimeline.tsx";
import { TimeOffPolicyApplyChangesModal } from "@/pages/settings/time-off/policy/widget/TimeOffPolicyApplyChangesModal.tsx";
import {
  usePolicyDelete,
  useTimeOffCategoryDetails,
  useTimeOffPolicyAssignToPreview,
  useTimeOffPolicyDetails,
  useTimeOffPolicySave,
} from "@/queries/use-time-off.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { RuleGroup } from "@/types/rule.types.ts";
import { ruleGroupsSchema } from "@/components/apply-to/rule-groups.schema.ts";
import {
  ApiTimeOffCarryoverCycleDateData,
  ApiTimeOffCarryoverExpireDateValue,
  ApiTimeOffCategoryType,
  ApiTimeOffCountDaysType,
  ApiTimeOffDayAmountType,
  ApiTimeOffDateType,
  ApiTimeOffPolicyCarryoverCycleDateType,
  ApiTimeOffPolicyMilestone,
  ApiTimeOffPolicyMilestoneCarryoverType,
  ApiTimeOffPolicyMilestoneTransferType,
  ApiTimeOffPolicyStartBalanceType,
  ApiTimeOffPolicyTerminationAccrualType,
  ApiTimeOffPolicyVersion,
  ApiTimeOffPolicyVersionPayload,
  findCurrentTimeOffPolicyVersion,
  getTimeOffPolicyAssignToGroups,
} from "@/types/time-off/time-off-policy.types.ts";
import { addDays, startOfDay, subDays } from "date-fns";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Resolver, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { TimeOffPolicyCountDays } from "@/pages/settings/time-off/policy/components/TimeOffPolicyCountDaysType.tsx";
import { TimeOffPolicyAccrualCycles } from "@/pages/settings/time-off/policy/components/TimeOffPolicyAccrualCycles.tsx";
import { TimeOffPolicyAccrueSettings } from "@/pages/settings/time-off/policy/components/TimeOffPolicyAccrueSettings.tsx";
import { parseSalaryValue } from "@/helpers/utils.helper.ts";
import { buildMilestoneSchema } from "@/pages/settings/time-off/policy/time-off-policy.types.ts";

function parseAttachmentThreshold(value: string | number | null | undefined) {
  return parseSalaryValue(typeof value === "string" ? value.replace(",", ".") : value);
}

export interface TimeOffCategoryPolicySchema {
  title: string;
  limits_negative_balance_allowed: boolean;
  negative_balance_in_cycle_allowed: boolean;
  accrual_confirm_required: boolean;

  daily_amount_enabled: boolean;
  min_daily_amount: number | null;

  request_total_enabled: boolean;
  min_request_total: number | null;
  max_request_total: number | null;

  notice_period_enabled: boolean;
  attachment_required_enabled: boolean;
  attachment_required_from_request_total: number | null;
  attachment_required_from_cycle_total: number | null;
  notice_period_days_type: ApiTimeOffCountDaysType;
  min_notice_period: number | null;
  max_notice_period: number | null;

  overlap_requests_allowed: boolean;
  probation_requests_allowed: boolean;
  past_requests_allowed: boolean;

  start_balance_type: ApiTimeOffPolicyStartBalanceType;
  termination_accrual_type: ApiTimeOffPolicyTerminationAccrualType;
  milestone_transfer_type: ApiTimeOffPolicyMilestoneTransferType;

  assign_to_groups: RuleGroup[];
  milestones: ApiTimeOffPolicyMilestone[];

  carryover_cycle_date_type: ApiTimeOffPolicyCarryoverCycleDateType;
  carryover_cycle_date_data: ApiTimeOffCarryoverCycleDateData | null;
  carryover_expire_enable: boolean;
  carryover_expire_date_type: ApiTimeOffDateType | null;
  carryover_expire_date_value: ApiTimeOffCarryoverExpireDateValue | null;
  carryover_type: ApiTimeOffPolicyMilestoneCarryoverType;
  carryover_amount: number;
  count_days_type: ApiTimeOffCountDaysType;
  day_amount_type: ApiTimeOffDayAmountType;
  contractual_fte_proration_enabled: boolean;
}

export function TimeOffCategoryPolicyEdit() {
  const { openModal } = useModals();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const assignToConditionTypes = useRuleConditionTypes(RuleRegistryPlace.timeOffPolicyAssignTo);
  const { id: categoryId, policy_id: policyId } = useParams();
  const isEdit = !!policyId;
  const { data: timeOffCategory } = useTimeOffCategoryDetails(
    categoryId ? Number(categoryId) : undefined
  );
  const { data: timeOffPolicy } = useTimeOffPolicyDetails(policyId ? Number(policyId) : undefined);
  const policyDelete = usePolicyDelete();

  useTitle(timeOffPolicy?.title, timeOffCategory?.title, t("common:edit"));

  const TimeOffCategoryPolicyObject = z
    .object({
      title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
      limits_negative_balance_allowed: z.boolean({ error: t("errors:field_required") }),
      negative_balance_in_cycle_allowed: z.boolean({ error: t("errors:field_required") }),
      accrual_confirm_required: z.boolean({ error: t("errors:field_required") }),

      daily_amount_enabled: z.boolean().default(false),
      min_daily_amount: z
        .union([z.string(), z.number()])
        .nullable()
        .optional()
        .transform(parseSalaryValue)
        .pipe(z.number().max(999).nullable()),

      request_total_enabled: z.boolean().default(false),
      min_request_total: z
        .union([z.string(), z.number()])
        .nullable()
        .optional()
        .transform(parseSalaryValue)
        .pipe(z.number().max(999).nullable()),
      max_request_total: z
        .union([z.string(), z.number()])
        .nullable()
        .optional()
        .transform(parseSalaryValue)
        .pipe(z.number().max(999).nullable()),

      notice_period_enabled: z.boolean().default(false),
      attachment_required_enabled: z.boolean().default(false),
      attachment_required_from_request_total: z
        .union([z.string(), z.number()])
        .nullable()
        .optional()
        .transform(parseAttachmentThreshold),
      attachment_required_from_cycle_total: z
        .union([z.string(), z.number()])
        .nullable()
        .optional()
        .transform(parseAttachmentThreshold),
      notice_period_days_type: z.enum(ApiTimeOffCountDaysType, {
        error: t("errors:field_required"),
      }),
      min_notice_period: z
        .union([z.string(), z.number()])
        .nullable()
        .optional()
        .transform(parseSalaryValue)
        .pipe(z.number().nullable()),
      max_notice_period: z
        .union([z.string(), z.number()])
        .nullable()
        .optional()
        .transform(parseSalaryValue)
        .pipe(z.number().nullable()),

      overlap_requests_allowed: z.boolean({ error: t("errors:field_required") }),
      probation_requests_allowed: z.boolean({ error: t("errors:field_required") }),
      past_requests_allowed: z.boolean({ error: t("errors:field_required") }),

      start_balance_type: z.enum(ApiTimeOffPolicyStartBalanceType, {
        error: t("errors:field_required"),
      }),
      termination_accrual_type: z.enum(ApiTimeOffPolicyTerminationAccrualType, {
        error: t("errors:field_required"),
      }),
      milestone_transfer_type: z.enum(ApiTimeOffPolicyMilestoneTransferType, {
        error: t("errors:field_required"),
      }),

      assign_to_groups: ruleGroupsSchema(),
      milestones: z.array(buildMilestoneSchema(), { error: t("errors:field_required") }),

      carryover_cycle_date_type: z.enum(ApiTimeOffPolicyCarryoverCycleDateType, {
        error: t("errors:field_required"),
      }),
      carryover_cycle_date_data: z
        .custom<ApiTimeOffCarryoverCycleDateData>()
        .nullable()
        .default(null),
      carryover_expire_enable: z.boolean({ error: t("errors:field_required") }),
      carryover_expire_date_type: z.enum(ApiTimeOffDateType).nullable().default(null),
      carryover_expire_date_value: z
        .custom<ApiTimeOffCarryoverExpireDateValue>()
        .nullable()
        .default(null),
      carryover_type: z.enum(ApiTimeOffPolicyMilestoneCarryoverType),
      carryover_amount: z
        .union([z.string(), z.number()])
        .nullable()
        .transform(parseSalaryValue)
        .pipe(z.number().nullable()),

      count_days_type: z.enum(ApiTimeOffCountDaysType, {
        error: t("errors:field_required"),
      }),
      day_amount_type: z.enum(ApiTimeOffDayAmountType, {
        error: t("errors:field_required"),
      }),
      contractual_fte_proration_enabled: z.boolean({ error: t("errors:field_required") }),
    })
    .superRefine((val, ctx) => {
      if (val.attachment_required_enabled) {
        const thresholds = [
          "attachment_required_from_request_total",
          "attachment_required_from_cycle_total",
        ] as const;
        for (const name of thresholds) {
          const threshold = val[name];
          if (threshold == null) continue;
          if (Number.isNaN(threshold))
            ctx.addIssue({
              code: "custom",
              message: t("errors:field_number_required"),
              path: [name],
            });
          else if (threshold < 0 || threshold > 999)
            ctx.addIssue({
              code: "custom",
              message: t("time_off:attachment_required_threshold_range"),
              path: [name],
            });
        }
        if (thresholds.every((name) => val[name] == null))
          ctx.addIssue({
            code: "custom",
            message: t("time_off:attachment_required_threshold_required"),
            path: [thresholds[0]],
          });
      }
      if (
        val.carryover_type === ApiTimeOffPolicyMilestoneCarryoverType.up_to &&
        val.carryover_amount == null
      ) {
        ctx.addIssue({
          code: "custom",
          message: t("errors:field_required"),
          path: ["carryover_amount"],
        });
      }
    });

  const formHook = useForm<TimeOffCategoryPolicySchema>({
    mode: "onSubmit",
    resolver: zodResolver(TimeOffCategoryPolicyObject) as Resolver<TimeOffCategoryPolicySchema>,
    defaultValues: {
      limits_negative_balance_allowed: false,
      negative_balance_in_cycle_allowed: false,
      accrual_confirm_required: true,

      daily_amount_enabled: false, // Check setting data for daily_amount
      request_total_enabled: false, // Check setting data for request_total
      notice_period_enabled: false, // Check setting data for notice_period
      notice_period_days_type: ApiTimeOffCountDaysType.calendar_days,
      attachment_required_enabled: false,
      attachment_required_from_request_total: null,
      attachment_required_from_cycle_total: null,

      overlap_requests_allowed: false,
      probation_requests_allowed: true,
      past_requests_allowed: false,

      start_balance_type: ApiTimeOffPolicyStartBalanceType.proportional_accrual,
      termination_accrual_type: ApiTimeOffPolicyTerminationAccrualType.proportional_accrual,
      milestone_transfer_type: ApiTimeOffPolicyMilestoneTransferType.immediately_on_condition,
      assign_to_groups: [],
      milestones: [],

      carryover_cycle_date_type: ApiTimeOffPolicyCarryoverCycleDateType.hire_date,
      carryover_cycle_date_data: null,
      carryover_expire_enable: false,
      carryover_expire_date_type: ApiTimeOffDateType.day,
      carryover_expire_date_value: null,
      carryover_type: ApiTimeOffPolicyMilestoneCarryoverType.none,
      carryover_amount: 0,

      count_days_type: ApiTimeOffCountDaysType.working_days,
      day_amount_type: ApiTimeOffDayAmountType.full_day,
      contractual_fte_proration_enabled: false,
    },
  });

  const {
    reset,
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = formHook;

  const assignToGroups = useWatch({ control, name: "assign_to_groups" }).filter(
    (group) => group.rules.length > 0
  );
  const assignToPreview = useTimeOffPolicyAssignToPreview(
    {
      category_id: Number(categoryId),
      policy_id: policyId ? Number(policyId) : null,
      assign_to_groups: assignToGroups,
    },
    !!categoryId && assignToGroups.length > 0
  );
  const takenByUpperCount = assignToPreview.data?.taken_by_upper_employee_ids.length ?? 0;

  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

  const currentVersion = (() => {
    if (!timeOffPolicy) return undefined;
    if (selectedVersionId != null) {
      const found = timeOffPolicy.versions.find((v) => v.id === selectedVersionId);
      if (found) return found;
    }
    return findCurrentTimeOffPolicyVersion(timeOffPolicy.versions);
  })();

  useEffect(() => {
    if (!timeOffPolicy || !currentVersion) return;

    const carryoverAmount = Number(currentVersion.carryover_amount ?? 0);

    reset({
      title: timeOffPolicy.title,
      assign_to_groups: getTimeOffPolicyAssignToGroups(currentVersion).map((group) => ({
        rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
      })),
      milestones: currentVersion.milestones,
      limits_negative_balance_allowed: timeOffPolicy.limits_negative_balance_allowed,
      negative_balance_in_cycle_allowed: timeOffPolicy.negative_balance_in_cycle_allowed,
      accrual_confirm_required: timeOffPolicy.accrual_confirm_required,
      carryover_cycle_date_type:
        currentVersion.carryover_cycle_date_type ??
        ApiTimeOffPolicyCarryoverCycleDateType.hire_date,
      carryover_cycle_date_data: currentVersion.carryover_cycle_date_data ?? null,
      carryover_expire_enable: currentVersion.carryover_expire_enable,
      carryover_expire_date_type: currentVersion.carryover_expire_date_type,
      carryover_expire_date_value: currentVersion.carryover_expire_date_value ?? null,
      carryover_type: currentVersion.carryover_enabled
        ? carryoverAmount > 0
          ? ApiTimeOffPolicyMilestoneCarryoverType.up_to
          : ApiTimeOffPolicyMilestoneCarryoverType.unlimited
        : ApiTimeOffPolicyMilestoneCarryoverType.none,
      carryover_amount: carryoverAmount,
      min_request_total: timeOffPolicy.min_request_total ?? 0,
      max_request_total: timeOffPolicy.max_request_total ?? 0,
      overlap_requests_allowed: timeOffPolicy.overlap_requests_allowed,
      probation_requests_allowed: timeOffPolicy.probation_requests_allowed,
      past_requests_allowed: timeOffPolicy.past_requests_allowed,
      request_total_enabled:
        (timeOffPolicy.min_request_total ?? 0) > 0 || (timeOffPolicy.max_request_total ?? 0) > 0,
      min_daily_amount: timeOffPolicy.min_daily_amount ?? 0,
      daily_amount_enabled: (timeOffPolicy.min_daily_amount ?? 0) > 0,
      min_notice_period: timeOffPolicy.min_notice_period ?? 0,
      attachment_required_enabled:
        timeOffPolicy.attachment_required_from_request_total != null ||
        timeOffPolicy.attachment_required_from_cycle_total != null,
      attachment_required_from_request_total: parseAttachmentThreshold(
        timeOffPolicy.attachment_required_from_request_total
      ),
      attachment_required_from_cycle_total: parseAttachmentThreshold(
        timeOffPolicy.attachment_required_from_cycle_total
      ),
      notice_period_days_type:
        timeOffPolicy.notice_period_days_type ?? ApiTimeOffCountDaysType.calendar_days,
      max_notice_period: timeOffPolicy.max_notice_period ?? 0,
      notice_period_enabled:
        (timeOffPolicy.min_notice_period ?? 0) > 0 || (timeOffPolicy.max_notice_period ?? 0) > 0,
      start_balance_type:
        currentVersion.start_balance_type ?? ApiTimeOffPolicyStartBalanceType.proportional_accrual,
      termination_accrual_type: currentVersion.termination_accrual_type,
      milestone_transfer_type:
        currentVersion.milestone_transfer_type ??
        ApiTimeOffPolicyMilestoneTransferType.immediately_on_condition,
      count_days_type: timeOffPolicy.count_days_type,
      day_amount_type: timeOffPolicy.day_amount_type,
      contractual_fte_proration_enabled: currentVersion.contractual_fte_proration_enabled,
    });
  }, [timeOffPolicy, currentVersion]);

  const policySave = useTimeOffPolicySave(control);
  const { renderDate } = useFormatter();

  const buildSubmitHandler = (
    mode: "update_current" | "new_version",
    dateEffectiveFrom: string | null = null,
    callbacks?: { onSuccess?: () => void; onError?: () => void },
    overrideVersionId?: number
  ) =>
    handleSubmit((dataSchema) => {
      if (!timeOffCategory) return;

      const versionIdForUpdate = overrideVersionId ?? currentVersion?.id;
      const isUpdatingCurrent = mode === "update_current" && versionIdForUpdate != null;

      const versionPayload: ApiTimeOffPolicyVersionPayload = {
        assign_to_groups: dataSchema.assign_to_groups.filter((group) => group.rules.length > 0),
        milestones: dataSchema.milestones,
        carryover_cycle_date_type: dataSchema.carryover_cycle_date_type,
        carryover_cycle_date_data: dataSchema.carryover_cycle_date_data,
        carryover_expire_enable: dataSchema.carryover_expire_enable,
        carryover_expire_date_type: dataSchema.carryover_expire_date_type,
        carryover_expire_date_value: dataSchema.carryover_expire_date_value,
        carryover_enabled:
          dataSchema.carryover_type !== ApiTimeOffPolicyMilestoneCarryoverType.none,
        carryover_amount:
          dataSchema.carryover_type === ApiTimeOffPolicyMilestoneCarryoverType.unlimited
            ? 0
            : (dataSchema.carryover_amount ?? 0),
        start_balance_type: dataSchema.start_balance_type,
        termination_accrual_type: dataSchema.termination_accrual_type,
        milestone_transfer_type: dataSchema.milestone_transfer_type,
        contractual_fte_proration_enabled: dataSchema.contractual_fte_proration_enabled,
        ...(dateEffectiveFrom !== null ? { date_effective_from: dateEffectiveFrom } : {}),
      };

      policySave.mutate(
        {
          id: timeOffPolicy?.id ?? 0,
          payload: {
            title: dataSchema.title,
            category_id: timeOffCategory.id,
            limits_negative_balance_allowed: dataSchema.limits_negative_balance_allowed,
            negative_balance_in_cycle_allowed: dataSchema.negative_balance_in_cycle_allowed,
            accrual_confirm_required: dataSchema.accrual_confirm_required,
            min_request_total: dataSchema.request_total_enabled
              ? (dataSchema.min_request_total ?? 0)
              : null,
            max_request_total: dataSchema.request_total_enabled
              ? (dataSchema.max_request_total ?? 0)
              : null,
            min_daily_amount: dataSchema.daily_amount_enabled
              ? (dataSchema.min_daily_amount ?? 0)
              : null,
            min_notice_period: dataSchema.notice_period_enabled
              ? (dataSchema.min_notice_period ?? 0)
              : null,
            notice_period_days_type: dataSchema.notice_period_days_type,
            attachment_required_from_request_total: dataSchema.attachment_required_enabled
              ? dataSchema.attachment_required_from_request_total
              : null,
            attachment_required_from_cycle_total: dataSchema.attachment_required_enabled
              ? dataSchema.attachment_required_from_cycle_total
              : null,
            max_notice_period: dataSchema.notice_period_enabled
              ? (dataSchema.max_notice_period ?? 0)
              : null,
            overlap_requests_allowed: dataSchema.overlap_requests_allowed,
            probation_requests_allowed: dataSchema.probation_requests_allowed,
            past_requests_allowed: dataSchema.past_requests_allowed,
            count_days_type: dataSchema.count_days_type,
            day_amount_type: dataSchema.day_amount_type,
            new_version: isUpdatingCurrent ? null : versionPayload,
            update_version: isUpdatingCurrent
              ? { version_id: versionIdForUpdate, version_update: versionPayload }
              : null,
          },
        },
        {
          onSuccess(createdPolicy) {
            analyticsService.trackEvent(
              isEdit ? analyticEvents.timeOffs.editedPolicy : analyticEvents.timeOffs.createdPolicy,
              {
                [analyticProperties.id]: createdPolicy.id,
              }
            );

            callbacks?.onSuccess?.();
            navigate(`/settings/time-off/categories/${categoryId}`);
          },
          onError() {
            callbacks?.onError?.();
          },
        }
      );
    });

  const onSubmitCreate = buildSubmitHandler("new_version", null);

  const currentVersionEffectiveDateObj = (() => {
    if (!currentVersion?.date_effective_from) return null;
    const parsed = new Date(currentVersion.date_effective_from);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  })();

  const currentVersionEffectiveDate = currentVersionEffectiveDateObj
    ? renderDate(currentVersionEffectiveDateObj)
    : null;

  const parseDate = (value: string | null | undefined): Date | undefined => {
    if (!value) return undefined;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const handleOpenNewVersionModal = () => {
    if (!timeOffCategory || !timeOffPolicy || !currentVersion) return;

    const timestamps = timeOffPolicy.versions
      .map((v) => (v.date_effective_from ? new Date(v.date_effective_from).getTime() : NaN))
      .filter((time) => !Number.isNaN(time));
    const latest = timestamps.length > 0 ? new Date(Math.max(...timestamps)) : null;
    const tomorrow = addDays(startOfDay(new Date()), 1);
    const latestPlusOne = latest ? addDays(startOfDay(latest), 1) : null;
    const minDate =
      latestPlusOne && latestPlusOne.getTime() > tomorrow.getTime() ? latestPlusOne : tomorrow;

    openModal(TimeOffPolicyApplyChangesModal, {
      categoryTitle: timeOffCategory.title,
      policyTitle: timeOffPolicy.title,
      minDate,
      employeesCount: currentVersion.employees_count,
      isSaving: policySave.isPending,
      onApply: (dateEffectiveFrom: string | null) =>
        new Promise<void>((resolve, reject) => {
          buildSubmitHandler("new_version", dateEffectiveFrom, {
            onSuccess: resolve,
            onError: reject,
          })();
        }),
    });
  };

  const submitVersionDateOnly = (
    version: ApiTimeOffPolicyVersion,
    dateEffectiveFrom: string,
    callbacks: { onSuccess: () => void; onError: () => void }
  ) => {
    if (!timeOffCategory || !timeOffPolicy) return;

    const versionPayload: ApiTimeOffPolicyVersionPayload = {
      assign_to_groups: getTimeOffPolicyAssignToGroups(version),
      milestones: version.milestones,
      carryover_cycle_date_type: version.carryover_cycle_date_type,
      carryover_cycle_date_data: version.carryover_cycle_date_data,
      carryover_expire_enable: version.carryover_expire_enable,
      carryover_expire_date_type: version.carryover_expire_date_type,
      carryover_expire_date_value: version.carryover_expire_date_value,
      carryover_enabled: version.carryover_enabled,
      carryover_amount: version.carryover_amount,
      start_balance_type:
        version.start_balance_type ?? ApiTimeOffPolicyStartBalanceType.proportional_accrual,
      termination_accrual_type: version.termination_accrual_type,
      milestone_transfer_type:
        version.milestone_transfer_type ??
        ApiTimeOffPolicyMilestoneTransferType.immediately_on_condition,
      contractual_fte_proration_enabled: version.contractual_fte_proration_enabled,
      date_effective_from: dateEffectiveFrom,
    };

    policySave.mutate(
      {
        id: timeOffPolicy.id,
        payload: {
          title: timeOffPolicy.title,
          category_id: timeOffCategory.id,
          limits_negative_balance_allowed: timeOffPolicy.limits_negative_balance_allowed,
          negative_balance_in_cycle_allowed: timeOffPolicy.negative_balance_in_cycle_allowed,
          accrual_confirm_required: timeOffPolicy.accrual_confirm_required,
          min_request_total: timeOffPolicy.min_request_total,
          max_request_total: timeOffPolicy.max_request_total,
          min_daily_amount: timeOffPolicy.min_daily_amount,
          min_notice_period: timeOffPolicy.min_notice_period,
          attachment_required_from_request_total: parseAttachmentThreshold(
            timeOffPolicy.attachment_required_from_request_total
          ),
          attachment_required_from_cycle_total: parseAttachmentThreshold(
            timeOffPolicy.attachment_required_from_cycle_total
          ),
          notice_period_days_type:
            timeOffPolicy.notice_period_days_type ?? ApiTimeOffCountDaysType.calendar_days,
          max_notice_period: timeOffPolicy.max_notice_period,
          overlap_requests_allowed: timeOffPolicy.overlap_requests_allowed,
          probation_requests_allowed: timeOffPolicy.probation_requests_allowed,
          past_requests_allowed: timeOffPolicy.past_requests_allowed,
          count_days_type: timeOffPolicy.count_days_type,
          day_amount_type: timeOffPolicy.day_amount_type,
          new_version: null,
          update_version: { version_id: version.id, version_update: versionPayload },
        },
      },
      {
        onSuccess(createdPolicy) {
          analyticsService.trackEvent(analyticEvents.timeOffs.editedPolicy, {
            [analyticProperties.id]: createdPolicy.id,
          });
          callbacks.onSuccess();
          navigate(`/settings/time-off/categories/${categoryId}`);
        },
        onError() {
          callbacks.onError();
        },
      }
    );
  };

  const handleChangeEffectiveDate = (version: ApiTimeOffPolicyVersion) => {
    if (!timeOffCategory || !timeOffPolicy) return;

    setSelectedVersionId(version.id);

    const versions = timeOffPolicy.versions;
    const idx = versions.findIndex((v) => v.id === version.id);
    const newerNeighbor = idx > 0 ? versions[idx - 1] : undefined;
    const olderNeighbor = idx >= 0 ? versions[idx + 1] : undefined;
    const olderDate = parseDate(olderNeighbor?.date_effective_from);
    const newerDate = parseDate(newerNeighbor?.date_effective_from);
    const tomorrow = addDays(startOfDay(new Date()), 1);
    const olderPlusOne = olderDate ? addDays(startOfDay(olderDate), 1) : null;
    const minDate =
      olderPlusOne && olderPlusOne.getTime() > tomorrow.getTime() ? olderPlusOne : tomorrow;
    const maxDate = newerDate ? subDays(startOfDay(newerDate), 1) : undefined;
    const initialDate = parseDate(version.date_effective_from);

    openModal(TimeOffPolicyApplyChangesModal, {
      categoryTitle: timeOffCategory.title,
      policyTitle: timeOffPolicy.title,
      initialDate,
      minDate,
      maxDate,
      employeesCount: version.employees_count,
      isSaving: policySave.isPending,
      onApply: (dateEffectiveFrom: string | null) =>
        new Promise<void>((resolve, reject) => {
          if (dateEffectiveFrom == null) {
            reject(new Error("date_effective_from is required"));
            return;
          }
          submitVersionDateOnly(version, dateEffectiveFrom, {
            onSuccess: resolve,
            onError: reject,
          });
        }),
    });
  };

  const handleApplyToCurrentRules = () => {
    if (!timeOffCategory || !timeOffPolicy || !currentVersion) return;

    const initialDate = parseDate(currentVersion.date_effective_from);
    const hideDate = !initialDate;

    openModal(TimeOffPolicyApplyChangesModal, {
      categoryTitle: timeOffCategory.title,
      policyTitle: timeOffPolicy.title,
      initialDate,
      isDateDisabled: !hideDate,
      hideDate,
      employeesCount: currentVersion.employees_count,
      isSaving: policySave.isPending,
      onApply: (dateEffectiveFrom: string | null) =>
        new Promise<void>((resolve, reject) => {
          buildSubmitHandler("update_current", dateEffectiveFrom, {
            onSuccess: resolve,
            onError: reject,
          })();
        }),
    });
  };

  const handleDelete = () => {
    if (!timeOffPolicy) return;
    if (policySave.isPending) return;

    openModal(ConfirmationModal, {
      question: t("common:delete"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await policyDelete.mutateAsync({
            policyId: timeOffPolicy.id,
            categoryId: timeOffPolicy.category_id,
          });

          navigate(`/settings/time-off/categories/${categoryId}`);
        } catch {
          return;
        }
      },
    });
  };

  if (Object.keys(errors).length) {
    console.log(errors);
  }

  return (
    <Page>
      <Page.Header
        title={t(isEdit ? "time_off:edit_policy_title" : "time_off:new_policy_title")}
        showBack={true}
        navigateTo={`/settings/time-off/categories/${categoryId}`}
      />
      <Page.Content>
        <Stack className={"px-16"} gap={"2xl"}>
          <Stack gap={"lg"}>
            <Input
              type={"text"}
              {...register("title")}
              error={errors.title?.message}
              label={t("common:title")}
              size={"lg"}
            />

            {isEdit &&
              timeOffPolicy &&
              timeOffPolicy.versions.length > 1 &&
              timeOffCategory?.category_type === ApiTimeOffCategoryType.accrues_time && (
                <TimeOffPolicyVersionsTimeline
                  policy={timeOffPolicy}
                  periodTrackType={timeOffCategory?.period_track_type}
                  selectedVersionId={currentVersion?.id ?? null}
                  onEditVersion={(v) => setSelectedVersionId(v.id)}
                  onChangeEffectiveDate={handleChangeEffectiveDate}
                />
              )}

            {timeOffCategory?.category_type == ApiTimeOffCategoryType.accrues_time && (
              <>
                <TimeOffPolicyAccrualCycles formHook={formHook} />
                <TimeOffPolicyAccrueSettings
                  formHook={formHook}
                  periodTrackType={timeOffCategory?.period_track_type}
                  currentVersion={currentVersion}
                />
                <TimeOffPolicyMilestones
                  formHook={formHook}
                  periodTrackType={timeOffCategory?.period_track_type}
                />
                <TimeOffPolicyCarryoverExpiration
                  formHook={formHook}
                  periodTrackType={timeOffCategory?.period_track_type}
                />
              </>
            )}

            <TimeOffPolicyCountDays
              formHook={formHook}
              periodTrackType={timeOffCategory?.period_track_type}
            />
            <TimeOffPolicyLimits
              formHook={formHook}
              periodTrackType={timeOffCategory?.period_track_type}
            />
            <TimeOffPolicyRequireConfirmation formHook={formHook} />
            <TimeOffPolicyStartBalance
              formHook={formHook}
              periodTrackType={timeOffCategory?.period_track_type}
            />
            <TimeOffPolicyTerminationAccrual formHook={formHook} />
            <TimeOffPolicyMilestoneTransfer formHook={formHook} />
          </Stack>

          <Stack direction={"vertical"} gap={"md"}>
            <Title header={t("core:assign_employees")} size={"lg"} paddingBottom paddingTop />

            <RuleGroupsEditor
              control={formHook.control}
              fieldName={"assign_to_groups"}
              setValue={formHook.setValue}
              place={RuleRegistryPlace.timeOffPolicyAssignTo}
              conditionTypes={assignToConditionTypes.conditionTypes}
              error={formHook.formState.errors.assign_to_groups?.message}
            />
            {takenByUpperCount > 0 && (
              <Text className={"text-secondary"}>
                {t("time_off:policy_assign_taken_by_upper_count", { count: takenByUpperCount })}
              </Text>
            )}
          </Stack>

          <Stack className={"pt-(--gap-md)"} gap={"md"}>
            <ServerErrorField errors={errors} />
            {isEdit && currentVersion ? (
              timeOffCategory?.category_type === ApiTimeOffCategoryType.accrues_time ? (
                <DropdownMenu
                  placement={"top"}
                  className={"w-full"}
                  trigger={
                    <Button size={"lg"} isLoading={policySave.isPending} className={"w-full"}>
                      {t("time_off:apply_changes")}...
                    </Button>
                  }
                >
                  <DropdownMenuItem
                    title={t("time_off:apply_to_current_rules")}
                    description={
                      currentVersionEffectiveDate
                        ? t("time_off:apply_to_current_rules_caption", {
                            date: currentVersionEffectiveDate,
                          })
                        : undefined
                    }
                    onClick={handleApplyToCurrentRules}
                  />
                  <DropdownMenuItem
                    title={t("time_off:create_new_rules_from")}
                    description={t("time_off:choose_effective_date_caption")}
                    onClick={handleOpenNewVersionModal}
                  />
                </DropdownMenu>
              ) : (
                <Button
                  size={"lg"}
                  type={"submit"}
                  onClick={buildSubmitHandler("update_current", null)}
                  isLoading={policySave.isPending}
                >
                  {t("common:apply")}
                </Button>
              )
            ) : (
              <Button
                size={"lg"}
                type={"submit"}
                onClick={onSubmitCreate}
                isLoading={policySave.isPending}
              >
                {t("common:apply")}
              </Button>
            )}
            {isEdit && (
              <Button
                variant={"danger"}
                size={"lg"}
                type={"submit"}
                onClick={handleDelete}
                isLoading={policySave.isPending}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-xmark"} />}
              >
                {t("common:delete")}
              </Button>
            )}
          </Stack>
        </Stack>
      </Page.Content>
    </Page>
  );
}
